import type { TFunction } from 'i18next';
import { UNIT_KEYS, unitKeyOf, type UnitKey } from '../../logistics/index.js';

/**
 * The unit vocabulary as the pickers and the page render it — shared by the
 * stock snapshot dialog and the delivery order dialog. The vocabulary itself,
 * and the identity rule (`unitKeyOf` / `sameUnit`), live server-safe in
 * `shared/logistics/units`; this file only adds the translation.
 *
 * The i18n namespace stays `logistics.stockDialog.units.*` because that is
 * where these labels have always lived; the delivery dialog borrows it
 * deliberately rather than duplicating eighteen keys across two locales.
 */
export { UNIT_KEYS, unitKeyOf, unitIdentity, sameUnit, unitToStore, isCountedUnit, type UnitKey } from '../../logistics/index.js';

/** The translator shape these helpers need — i18next's `t` fits it. */
export type Translate = (key: string) => string;

const labelOf = (t: Translate, key: UnitKey): string => t(`logistics.stockDialog.units.${key}`);

/** A picker's options, in list order: the KEY is what a write stores, the
 *  label what the viewer reads. */
export function unitChoices(t: Translate): { value: UnitKey; label: string }[] {
  return UNIT_KEYS.map((key) => ({ value: key, label: labelOf(t, key) }));
}

/** Localised unit labels, in list order. Kept for pickers that still store the
 *  label; a new write should store the key from `unitChoices`. */
export function unitOptions(t: TFunction): string[] {
  return unitChoices(t).map((choice) => choice.label);
}

/**
 * A stored unit as the viewer reads it: a known unit — its key or a legacy
 * label in either language — in the viewer's language; free text the
 * vocabulary does not know, exactly as it was typed.
 */
export function unitLabel(t: Translate, stored: string): string {
  const key = unitKeyOf(stored);
  return key === null ? stored : labelOf(t, key);
}
